import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Upload, Check } from "lucide-react";

export default function Post() {
  const [searchParams] = useSearchParams();
  const { code } = useParams();  // for /invite/:code route
  const navigate = useNavigate();
  const [mode, setMode] = useState(searchParams.get("mode") === "self" ? "self" : "other"); // "other" | "self"
  const [albums, setAlbums] = useState([]);
  const [albumId, setAlbumId] = useState(searchParams.get("album") || "");
  const [target, setTarget] = useState(searchParams.get("to") || "");
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [dateTaken, setDateTaken] = useState("");
  const [dateMode, setDateMode] = useState("date"); // "date" | "year"
  const [location, setLocation] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [inviteOwner, setInviteOwner] = useState(null);

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const lastQueriedRef = useRef("");

  // Load the user's own albums (for "post to my album" mode). Skipped on invite links.
  useEffect(() => {
    if (code) return;
    (async () => {
      try {
        const { data } = await api.get("/albums");
        setAlbums(data.albums || []);
      } catch { /* ignore */ }
    })();
  }, [code]);

  React.useEffect(() => {
    if (code) {
      (async () => {
        try {
          const { data } = await api.get(`/invites/${code}`);
          setInviteOwner(data.owner_screen_name);
          setTarget(data.owner_screen_name);
        } catch {
          setError("Invalid or expired invite.");
        }
      })();
    }
  }, [code]);

  // Debounced suggestion fetch
  useEffect(() => {
    if (code) return; // invite locks the field, no autocomplete
    if (!target || target.length < 1) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      // skip if last fetched the same query
      if (lastQueriedRef.current === target) return;
      lastQueriedRef.current = target;
      try {
        const { data } = await api.get("/users/suggest", { params: { q: target } });
        setSuggestions(data.suggestions || []);
        setActiveIdx(-1);
      } catch { /* ignore */ }
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [target, code]);

  const pickSuggestion = (s) => {
    setTarget(s.screen_name);
    setShowSuggestions(false);
    setSuggestions([]);
    lastQueriedRef.current = s.screen_name;
  };

  const onKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (caption) fd.append("caption", caption);
      if (dateTaken) fd.append("date_taken", dateTaken);
      if (location) fd.append("location", location);
      if (mode === "self" && !code) {
        fd.append("album_id", albumId);
        await api.post("/photos/upload-self", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        fd.append("target_screen_name", target);
        await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setSuccess(true);
      setTimeout(() => {
        if (mode === "self" && !code && albumId) navigate(`/album/${albumId}`);
        else if (!code) navigate("/dashboard");
      }, 1600);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="paper-grain min-h-screen" data-testid="post-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <p className="font-script text-2xl text-[#3A5A40]">share a memory</p>
        <h1 className="font-serif-display text-4xl mb-2">Post a photo</h1>
        {inviteOwner ? (
          <div className="mb-10">
            <p className="text-[#5C5C5C] mb-4">
              You're posting via an invite from <strong>@{inviteOwner}</strong>. The photo will arrive in their pending review.
            </p>
            <div className="paper-card p-5 bg-[#F9F6F0] border-l-4 border-[#3A5A40]" data-testid="invite-explainer">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3A5A40] mb-2">How this works</p>
              <ul className="text-sm text-[#5C5C5C] space-y-1.5 list-disc pl-5">
                <li><strong>@{inviteOwner}</strong> asked you to share a photo <em>of them</em>.</li>
                <li>Pick a picture below and send it — that's all you need to do.</li>
                <li>It lands privately in <strong>@{inviteOwner}</strong>'s review queue. They choose whether to keep it.</li>
                <li>It is never posted publicly without their approval.</li>
              </ul>
            </div>
          </div>
        ) : (
          <>
            {/* Mode toggle: post to someone else vs to your own album */}
            <div className="flex items-center gap-1 bg-[#F0EBE1] p-1 rounded-full w-fit mb-4" data-testid="post-mode-toggle">
              <button
                type="button"
                onClick={() => { setMode("other"); setError(""); }}
                data-testid="post-mode-other"
                className={`text-sm px-4 py-1.5 rounded-full transition-colors ${mode === "other" ? "bg-white shadow text-[#8C3A3A]" : "text-[#5C5C5C] hover:text-[#1F1F1F]"}`}
              >
                Photo of someone else
              </button>
              <button
                type="button"
                onClick={() => { setMode("self"); setError(""); }}
                data-testid="post-mode-self"
                className={`text-sm px-4 py-1.5 rounded-full transition-colors ${mode === "self" ? "bg-white shadow text-[#8C3A3A]" : "text-[#5C5C5C] hover:text-[#1F1F1F]"}`}
              >
                To one of my albums
              </button>
            </div>
            <p className="text-[#5C5C5C] mb-10">
              {mode === "other"
                ? "Enter the screen name of the person in the photo. It will arrive in their pending queue for review."
                : "Add a photo straight to one of your own albums — it's published there right away (no review needed)."}
            </p>
          </>
        )}

        <form onSubmit={onSubmit} className="paper-card p-8 space-y-4">
          {mode === "self" && !code ? (
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2">Album *</label>
              {albums.length === 0 ? (
                <p className="text-sm text-[#8A8A8A]" data-testid="post-no-albums">
                  You don't have any albums yet.{" "}
                  <button type="button" onClick={() => navigate("/dashboard")} className="underline text-[#8C3A3A]">
                    Create one on your dashboard
                  </button>{" "}
                  first.
                </p>
              ) : (
                <select
                  data-testid="post-album-select"
                  required
                  className="mp-input"
                  value={albumId}
                  onChange={(e) => setAlbumId(e.target.value)}
                >
                  <option value="">Choose an album…</option>
                  {albums.map((a) => (
                    <option key={a.id} value={a.id}>{a.title}{a.is_public ? " (public)" : " (private)"}</option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2">Recipient screen name *</label>
              <div className="relative">
                <input
                  data-testid="post-target-input"
                  required
                  className="mp-input"
                  value={target}
                  onChange={(e) => { setTarget(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  onKeyDown={onKeyDown}
                  disabled={!!code}
                  autoComplete="off"
                />
                {showSuggestions && !code && suggestions.length > 0 && (
                  <ul
                    data-testid="post-target-suggestions"
                    className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-[#E5E0D8] shadow-lg max-h-72 overflow-y-auto"
                  >
                    {suggestions.map((s, i) => (
                      <li
                        key={s.id}
                        data-testid={`suggest-${s.screen_name}`}
                        onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                        onMouseEnter={() => setActiveIdx(i)}
                        className={`px-3 py-2 cursor-pointer flex items-baseline gap-2 ${i === activeIdx ? "bg-[#F0EBE1]" : "hover:bg-[#F9F6F0]"}`}
                      >
                        <span className="font-serif-display text-base">@{s.screen_name}</span>
                        {s.name && <span className="text-xs text-[#8A8A8A] truncate">{s.name}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2">Photo *</label>
            <input data-testid="post-file-input" type="file" accept="image/*" required onChange={(e) => setFile(e.target.files[0])} className="mp-input"/>
            <p className="text-xs text-[#8A8A8A] mt-1">Will be resized to 1200px max width.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2">Caption (optional)</label>
            <input data-testid="post-caption-input" className="mp-input" value={caption} onChange={(e) => setCaption(e.target.value)}/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <label className="text-xs uppercase tracking-[0.18em] font-semibold">Date taken (optional)</label>
                <div className="flex items-center gap-1 bg-[#F0EBE1] p-0.5 rounded-full" data-testid="date-mode-toggle">
                  <button
                    type="button"
                    onClick={() => { setDateMode("date"); setDateTaken(""); }}
                    data-testid="date-mode-date"
                    className={`text-xs px-3 py-1 rounded-full transition-colors ${dateMode === "date" ? "bg-white shadow text-[#8C3A3A]" : "text-[#5C5C5C]"}`}
                  >
                    Full date
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDateMode("year"); setDateTaken(""); }}
                    data-testid="date-mode-year"
                    className={`text-xs px-3 py-1 rounded-full transition-colors ${dateMode === "year" ? "bg-white shadow text-[#8C3A3A]" : "text-[#5C5C5C]"}`}
                  >
                    Year only
                  </button>
                </div>
              </div>
              {dateMode === "year" ? (
                <input
                  data-testid="post-year-input"
                  type="number"
                  min="1900"
                  max="2100"
                  step="1"
                  placeholder="e.g. 2015"
                  className="mp-input"
                  value={dateTaken}
                  onChange={(e) => setDateTaken(e.target.value)}
                />
              ) : (
                <input data-testid="post-date-input" type="date" className="mp-input" value={dateTaken} onChange={(e) => setDateTaken(e.target.value)}/>
              )}
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2">Location (optional)</label>
              <input data-testid="post-location-input" className="mp-input" placeholder="e.g. Lake Tahoe" value={location} onChange={(e) => setLocation(e.target.value)}/>
            </div>
          </div>
          {error && <p data-testid="post-error" className="text-sm text-[#8C3A3A]">{error}</p>}
          {success && (
            <p data-testid="post-success" className="text-sm text-[#3A5A40] flex items-center gap-2">
              <Check className="w-4 h-4"/> {mode === "self" && !code ? "Photo added to your album!" : "Photo sent! It will appear in their pending queue."}
            </p>
          )}
          <button
            data-testid="post-submit-btn"
            type="submit"
            disabled={loading || !file || (mode === "self" && !code ? !albumId : !target)}
            className="btn-primary"
          >
            <Upload className="w-4 h-4"/> {loading ? "Uploading..." : (mode === "self" && !code ? "Add to album" : "Send photo")}
          </button>
        </form>
      </div>
    </div>
  );
}
